import React, {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {FaBars} from "react-icons/fa";
import TopNav from "../navigation/TopNavBar";
import SideNav from "../navigation/SideNavBar";
import Footer from "../navigation/Footer";

const SIDE_NAV_COLLAPSED_KEY = "sideNavCollapsed";

export interface ShellConfig {
    showFooter: boolean;
    showSideNav: boolean;
    showTopNavUserControls: boolean;
}

const DEFAULT_CONFIG: ShellConfig = {
    showFooter: true,
    showSideNav: true,
    showTopNavUserControls: true
};

interface ShellContextValue {
    registerLayout: (config: ShellConfig) => () => void;
    holdChrome: () => () => void;
}

const ShellContext = createContext<ShellContextValue | null>(null);

/**
 * Registers the mounted page as one that wants the app chrome (top nav, side nav,
 * footer). Called by MainLayout; the chrome itself lives in LayoutShell and is
 * never unmounted on navigation, so the side nav keeps its state and scroll.
 */
export const useRegisterLayout = (config: ShellConfig) => {
    const ctx = useContext(ShellContext);
    const {showFooter, showSideNav, showTopNavUserControls} = config;

    useEffect(() => {
        if (!ctx) return;
        return ctx.registerLayout({showFooter, showSideNav, showTopNavUserControls});
    }, [ctx, showFooter, showSideNav, showTopNavUserControls]);
};

/**
 * Suspense fallback wrapper: keeps the chrome on screen while a lazy page chunk
 * loads, so only the main content area shows the loader.
 */
export const ChromeHold: React.FC<{children: ReactNode}> = ({children}) => {
    const ctx = useContext(ShellContext);

    useEffect(() => {
        if (!ctx) return;
        return ctx.holdChrome();
    }, [ctx]);

    return <>{children}</>;
};

/**
 * Renders the persistent application shell around the routed page content.
 * Chrome is shown while at least one page has registered a layout, or while a
 * lazy chunk is loading.
 */
const LayoutShell: React.FC<{children: ReactNode}> = ({children}) => {
    const [layouts, setLayouts] = useState<ShellConfig[]>([]);
    const [holds, setHolds] = useState(0);

    const registerLayout = useCallback((config: ShellConfig) => {
        setLayouts((prev) => [...prev, config]);
        return () => setLayouts((prev) => {
            const index = prev.indexOf(config);
            return index === -1 ? prev : [...prev.slice(0, index), ...prev.slice(index + 1)];
        });
    }, []);

    const holdChrome = useCallback(() => {
        setHolds((prev) => prev + 1);
        return () => setHolds((prev) => prev - 1);
    }, []);

    const value = useMemo(() => ({registerLayout, holdChrome}), [registerLayout, holdChrome]);

    // Remember the last active config so the chrome does not flicker while a
    // lazy chunk loads between two pages that both use the layout.
    const [lastConfig, setLastConfig] = useState<ShellConfig>(DEFAULT_CONFIG);
    const activeConfig = layouts.length > 0 ? layouts[layouts.length - 1] : null;

    useEffect(() => {
        if (activeConfig) setLastConfig(activeConfig);
    }, [activeConfig]);

    const config = activeConfig ?? lastConfig;

    // Side-nav collapse is a deliberate user preference, so it persists across
    // navigations and reloads rather than resetting on every page.
    const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
        try {
            return localStorage.getItem(SIDE_NAV_COLLAPSED_KEY) === "true";
        } catch {
            return false;
        }
    });

    const toggleNav = useCallback(() => {
        setNavCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(SIDE_NAV_COLLAPSED_KEY, String(next));
            } catch {
                // storage unavailable — the preference simply won't persist
            }
            return next;
        });
    }, []);

    // Hiding is deferred by a tick so the gap between the outgoing page
    // unregistering and the incoming one registering never unmounts the chrome.
    const [showChrome, setShowChrome] = useState(false);

    // A Suspense hold can only keep chrome that is already on screen; it must
    // never turn it on, or the initial load of a chrome-less page (login,
    // registration, MFA) would flash the top and side nav.
    const wantsChrome = activeConfig !== null || (holds > 0 && showChrome);

    useEffect(() => {
        if (wantsChrome) {
            setShowChrome(true);
            return;
        }
        const timer = setTimeout(() => setShowChrome(false), 0);
        return () => clearTimeout(timer);
    }, [wantsChrome]);

    if (!showChrome) {
        return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
    }

    return (
        <ShellContext.Provider value={value}>
            <div className="layout">
                <div>
                    <TopNav showUserControls={config.showTopNavUserControls}/>
                </div>
                <div className={`layout-body${config.showSideNav && navCollapsed ? " layout-body--nav-collapsed" : ""}`}>
                    {config.showSideNav && !navCollapsed && (
                        <div>
                            <SideNav/>
                        </div>
                    )}
                    {config.showSideNav && (
                        <button
                            type="button"
                            className="side-nav-toggle"
                            onClick={toggleNav}
                            aria-expanded={!navCollapsed}
                            aria-label={navCollapsed ? "Expand navigation" : "Collapse navigation"}
                        >
                            <FaBars/>
                            <span className="side-nav-toggle__tooltip">
                                {navCollapsed ? "Expand Navigation" : "Collapse Navigation"}
                            </span>
                        </button>
                    )}
                    <main className="main-content" style={!config.showSideNav ? {width: "100%"} : undefined}>
                        {children}
                    </main>
                </div>
                {config.showFooter && <Footer/>}
            </div>
        </ShellContext.Provider>
    );
};

export default LayoutShell;
